// Inquiry cart (询价车) — a client-side collection of products/fittings the
// customer is interested in, gathered for a SINGLE consolidated quote request.
// It is NOT a checkout cart: no payment, no stock. Each line gets its own
// human-readable order number so staff (and the assistant) can reference a
// specific interest like "@XS-PPRW-260711-1430-A3F".
//
// State is module-scope reactive + persisted to localStorage, mirroring the
// quoteDraft store pattern. new Date()/crypto are app-runtime APIs (fine here).

import { reactive, computed } from 'vue'

const STORE_KEY = 'xs-cart-v1'

// Pre-stored product serial codes — the order-number prefix. Encoding the
// product line up front lets us group/lazy-load by line and pick the thumbnail.
export const PRODUCT_CODE = {
  ppr_water: 'PPRW',
  pe_water: 'PEW',
  pvc_u_water: 'PVCW',
  pvc_u_drainage: 'PVCD',
  pvc_conduit: 'PVCC',
  ppr_household: 'PPRH'
}
export const PRODUCT_IDS = Object.keys(PRODUCT_CODE)

function pad(n) { return String(n).padStart(2, '0') }

// XS-{CODE}-{YYMMDD}-{HHMM}-{3 base36 rand}
// e.g. XS-PPRW-260711-1430-A3F. Sortable/cacheable by product then time.
export function makeOrderNo(productId, when) {
  const d = when || new Date()
  const code = PRODUCT_CODE[productId] || 'XS'
  const date = `${pad(d.getFullYear() % 100)}${pad(d.getMonth() + 1)}${pad(d.getDate())}`
  const time = `${pad(d.getHours())}${pad(d.getMinutes())}`
  let rand = ''
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789' // no ambiguous 0/O/1/I
  const bytes = (globalThis.crypto && crypto.getRandomValues)
    ? crypto.getRandomValues(new Uint8Array(3))
    : [Math.floor(Math.random() * 256), Math.floor(Math.random() * 256), Math.floor(Math.random() * 256)]
  for (const b of bytes) rand += alphabet[b % alphabet.length]
  return `XS-${code}-${date}-${time}-${rand}`
}

// Thumbnail for an order line: prefer the specific fitting photo, fall back to
// the product-line category shot. Components can add an onerror → category too.
export function thumbnailFor(productId, fittingKey) {
  if (fittingKey) return `/images/fittings/${productId}/photo/${fittingKey}.png`
  return `/images/categories/${productId}.jpg`
}

function load() {
  try {
    const raw = localStorage.getItem(STORE_KEY)
    const arr = raw ? JSON.parse(raw) : []
    return Array.isArray(arr) ? arr.filter(it => it && PRODUCT_IDS.includes(it.productId) && it.orderNo) : []
  } catch (_) { return [] }
}

const state = reactive({ items: load() })

function persist() {
  try { localStorage.setItem(STORE_KEY, JSON.stringify(state.items)) } catch (_) { /* quota / private mode */ }
}

export const cartItems = computed(() => state.items)
export const cartCount = computed(() => state.items.length)

export function isInCart(productId, fittingKey, size) {
  return state.items.some(it =>
    it.kind === 'catalog' && it.productId === productId &&
    (it.fittingKey || null) === (fittingKey || null) && (it.size || null) === (size || null))
}

// Add a catalog interest (a product line, a fitting, or a specific size row).
// De-duplicates on (productId, fittingKey, size); returns the existing or new item.
export function addCatalogItem({ productId, fittingKey = null, fittingName = null, size = null, specRow = null, note = '' }) {
  if (!PRODUCT_IDS.includes(productId)) return null
  const existing = state.items.find(it =>
    it.kind === 'catalog' && it.productId === productId &&
    (it.fittingKey || null) === (fittingKey || null) && (it.size || null) === (size || null))
  if (existing) return existing
  const item = {
    orderNo: makeOrderNo(productId),
    kind: 'catalog',
    productId,
    productCode: PRODUCT_CODE[productId],
    fittingKey: fittingKey || null,
    fittingName: fittingName || null,
    size: size || null,
    specRow: specRow || null,
    note: note || '',
    thumbnail: thumbnailFor(productId, fittingKey),
    addedAt: new Date().toISOString()
  }
  state.items.push(item)
  persist()
  return item
}

// Add a DIY / custom-spec interest (from the chat DIY draft or a manual form).
export function addDiyItem({ productId, diy, note = '' }) {
  const pid = PRODUCT_IDS.includes(productId) ? productId : (PRODUCT_IDS.includes(diy && diy.category) ? diy.category : null)
  const item = {
    orderNo: makeOrderNo(pid || 'ppr_water'),
    kind: 'diy',
    productId: pid,
    productCode: pid ? PRODUCT_CODE[pid] : 'XS',
    diy: diy || {},
    note: note || '',
    thumbnail: pid ? thumbnailFor(pid, null) : '/images/categories/ppr_water.jpg',
    addedAt: new Date().toISOString()
  }
  state.items.push(item)
  persist()
  return item
}

export function updateItem(orderNo, patch) {
  const it = state.items.find(x => x.orderNo === orderNo)
  if (it) { Object.assign(it, patch); persist() }
  return it
}

export function removeItem(orderNo) {
  const i = state.items.findIndex(x => x.orderNo === orderNo)
  if (i !== -1) { state.items.splice(i, 1); persist() }
}

export function clearCart() { state.items.splice(0, state.items.length); persist() }

export function getItem(orderNo) { return state.items.find(x => x.orderNo === orderNo) || null }

// Compact, sanitized snapshot for the chat assistant / inquiry payload — no
// internal fields, capped, safe to send to the server.
export function cartSnapshot() {
  return state.items.slice(0, 30).map(it => ({
    orderNo: it.orderNo,
    productId: it.productId,
    kind: it.kind,
    fittingKey: it.fittingKey || undefined,
    size: it.size || undefined,
    diy: it.kind === 'diy' ? {
      fittingType: it.diy?.fittingType,
      sizes: Array.isArray(it.diy?.sizes) ? it.diy.sizes.slice(0, 10) : undefined,
      pressureClass: it.diy?.pressureClass,
      standard: it.diy?.standard,
      material: it.diy?.material,
      quantity: it.diy?.quantity
    } : undefined,
    note: it.note ? String(it.note).slice(0, 200) : undefined
  }))
}
